import { useState, useRef, useEffect } from "react";
import { Layout, Input, Button, Space, Typography, Avatar, Spin, message, List, Popconfirm, Empty, Tooltip } from "antd";
import { SendOutlined, RobotOutlined, UserOutlined, DeleteOutlined, PlusOutlined, MessageOutlined } from "@ant-design/icons";
import { sendChatMessage, sendChatMessageWithConfig, getConversations, getConversation, deleteConversation } from "../api/chatbot/ChatbotApi";
import ChartRenderer from './Chatbot/ChartRenderer';
import { ChartParser } from '../utils/ChartParser';
import { getMarkdownComponents } from '../utils/markdownComponents';
import SafeMarkdown from './SafeMarkdown';
import EnhancedChatInput from './Chatbot/EnhancedChatInput';
import './Chatbot/ChatMessage.css';

const { Sider, Content } = Layout;
const { Text, Title } = Typography;

function ChatInterfaceWithHistory({ configId = null, conversationId = null, onConversationChange = null, botType = 'GEM', onNewChat = null, initialMessageData = null, onInitialMessageSent = null, chatbotConfig = null, highlightNewConversation = false }) {
  // Add custom scrollbar styles
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .custom-scrollbar::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      .custom-scrollbar::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
      }
      .custom-scrollbar::-webkit-scrollbar-thumb:hover {
        background: #555;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(conversationId);
  const [messages, setMessages] = useState([]);
  
  // UI state
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [leftSiderWidth, setLeftSiderWidth] = useState(280);
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  const messagesEndRef = useRef(null);
  const leftSiderRef = useRef(null);
  const lastLoadedConversationId = useRef(null);





  const colors = {
    primary: "#F15A24",
    light: "rgba(241, 90, 36, 0.48)",
    border: "#F15A24",
    dark: "#201F1F",
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Handle conversation ID prop changes
  useEffect(() => {
    if (conversationId && conversationId !== lastLoadedConversationId.current) {
      loadConversation(conversationId);
    }
  }, [conversationId]);

  // Load conversations on mount and when configId changes
  useEffect(() => {
    if (configId) {
      loadConversations();
    }
  }, [configId]);

  const loadConversations = async () => {
    setLoadingConversations(true);
    try {
      const response = await getConversations(configId, 'GPT');
      
      // Filter conversations to only show those for the current config
      const filteredConversations = response.conversations.filter(conv => 
        !configId || conv.configId === configId
      );
      
      setConversations(filteredConversations);
      
      // Only load the most recent conversation if no conversationId prop is provided
      if (!conversationId && filteredConversations.length > 0 && !currentConversationId) {
        const firstConvId = filteredConversations[0].conversationId;
        lastLoadedConversationId.current = firstConvId;
        loadConversation(firstConvId);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      message.error('Failed to load conversations');
    } finally {
      setLoadingConversations(false);
    }
  };

  const loadConversation = async (convId) => {
    try {
      setCurrentConversationId(convId);
      lastLoadedConversationId.current = convId;
      
      const response = await getConversation(convId);
      
      setCurrentConversationId(convId);
      setMessages(response.messages || []);
    } catch (error) {
      console.error('Error loading conversation:', error);
      setCurrentConversationId(convId);
      setMessages([]);
    }
  };

  const handleNewConversation = async () => {
    if (onNewChat) {
      onNewChat();
    } else {
      // Generate a temporary conversation ID (not saved to DB)
      const tempConversationId = `temp_conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Create a temporary conversation entry for immediate display
      const tempConversation = {
        conversationId: tempConversationId,
        title: 'New Conversation', // Will be updated after first message
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        configId: configId,
        chatMode: 'GPT',
        isTemporary: true // Flag to indicate it's not saved yet
      };
      
      // Add to conversations list immediately
      setConversations(prev => [tempConversation, ...prev]);
      
      // Set as current conversation
      setCurrentConversationId(tempConversationId);
      setMessages([]);
      
      // Trigger conversation change callback
      if (onConversationChange) {
        onConversationChange(tempConversationId);
      }
    }
  };

  const sendInitialMessage = async (message, conversationId = null) => {
    const userMessage = {
      role: "user",
      content: message,
      timestamp: new Date().toISOString(),
    };

    // Show user message immediately, then show loading
    setMessages([userMessage]);
    setLoading(true);

    try {
      // Send the message to the API
      const response = configId 
        ? await sendChatMessageWithConfig(message, conversationId, configId, 'GPT')
        : await sendChatMessage(message, conversationId, 'GPT');

      const aiMessage = {
        role: "assistant",
        content: response.reply,
        timestamp: new Date().toISOString(),
        model: response.model,
      };

      setMessages([userMessage, aiMessage]);
      
      // Update conversation ID if it's a new conversation
      if (response.conversationId && response.conversationId !== conversationId) {
        setCurrentConversationId(response.conversationId);
        if (onConversationChange) {
          onConversationChange(response.conversationId);
        }
        // Reload conversations to show the new one
        loadConversations();
      }
    } catch (error) {
      console.error('Error sending initial message:', error);
      setMessages([
        userMessage,
        {
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again.",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      role: "user",
      content: inputValue,
      timestamp: new Date().toISOString(),
    };

    // Show user message immediately, then show loading
    setMessages(prev => [...(prev || []), userMessage]);
    const messageToSend = inputValue;
    setInputValue("");
    setLoading(true);

    try {
      // Check if this is a temporary conversation (not saved to DB yet)
      const isTemporaryConversation = currentConversationId?.startsWith('temp_conv_');
      const conversationIdToSend = isTemporaryConversation ? null : currentConversationId;
      
      const response = configId 
        ? await sendChatMessageWithConfig(messageToSend, conversationIdToSend, configId, 'GPT')
        : await sendChatMessage(messageToSend, conversationIdToSend, 'GPT');
      
      // If this was a temporary conversation or new conversation, update with real ID
      if (isTemporaryConversation || !currentConversationId || currentConversationId !== response.conversationId) {
        const newConvId = response.conversationId;
        setCurrentConversationId(newConvId);
        if (onConversationChange) {
          onConversationChange(newConvId);
        }
        
        // Remove temporary conversation and reload to get the real one with proper title
        if (isTemporaryConversation) {
          setConversations(prev => prev.filter(conv => conv.conversationId !== currentConversationId));
        }
        
        // Reload conversations to show the updated one with proper title
        loadConversations();
      } else {
        // Update existing conversation in the list with new title if it was a temp conversation
        const isFirstMessage = messages.length === 1; // Only user message exists
        if (isFirstMessage) {
          loadConversations(); // Reload to get the updated title from backend
        }
      }

      const aiMessage = {
        role: "assistant",
        content: response.reply,
        timestamp: new Date().toISOString(),
        model: response.model,
      };

      setMessages(prev => [...(prev || []), aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => [
        ...(prev || []),
        {
          role: "assistant",
          content: "Sorry, I encountered an error. Please try again.",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    try {
      await deleteConversation(conversationId);
      loadConversations();
      message.success('Conversation deleted');
      
      if (conversationId === currentConversationId) {
        handleNewConversation();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      message.error('Failed to delete conversation');
    }
  };

  // Handle initial message if provided
  useEffect(() => {
    if (initialMessageData && initialMessageData.message && !messages.length) {
      sendInitialMessage(initialMessageData.message, initialMessageData.conversationId);
      if (onInitialMessageSent) {
        onInitialMessageSent();
      }
    }
  }, [initialMessageData, messages.length]);

  // Mouse event handlers for resizing
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizingLeft(true);
  };

  const handleMouseMove = (e) => {
    if (isResizingLeft) {
      const newWidth = Math.max(200, Math.min(500, e.clientX));
      setLeftSiderWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    setIsResizingLeft(false);
  };

  useEffect(() => {
    if (isResizingLeft) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizingLeft]);

  return (
    <Layout style={{ height: "100vh", background: "#f5f5f5" }}>
      {/* Left Sidebar - Conversations */}
      <Sider
        ref={leftSiderRef}
        width={leftSiderWidth}
        style={{
          background: "white",
          borderRight: "2px solid rgba(241, 90, 36, 0.38)",
          position: "relative",
        }}
      >
        {/* Resize handle */}
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "4px",
            height: "100%",
            cursor: "col-resize",
            background: "transparent",
            zIndex: 10,
          }}
          onMouseDown={handleMouseDown}
        />
        
        {/* G.E.M Badge - REMOVED */}
        
        <div style={{ padding: "12px 16px", borderBottom: "2px solid rgba(241, 90, 36, 0.38)" }}>
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Title level={5} style={{ margin: 0, color: "#201F1F" }}>
              Conversations
            </Title>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              onClick={handleNewConversation}
              style={{ background: "#F15A24", borderColor: "#F15A24" }}
            >
              New
            </Button>
          </Space>
        </div>
        
        <div style={{ height: "calc(100vh - 160px)", overflow: "auto" }} className="custom-scrollbar">
        {loadingConversations ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <Spin />
          </div>
        ) : conversations.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No conversations yet"
            style={{ marginTop: "40px" }}
          />
        ) : (
          <List
            dataSource={conversations}
            renderItem={(conv) => (
              <List.Item
                style={{
                  padding: "8px",
                  cursor: "pointer",
                  margin: "4px 8px",
                  borderRadius: "10px",
                  backgroundColor: conv.conversationId === currentConversationId 
                    ? colors.light 
                    : "transparent",
                  border: conv.conversationId === currentConversationId 
                    ? `2px solid ${colors.border}` 
                    : "2px solid transparent",
                  transition: "all 0.3s ease",
                  transform: conv.conversationId === currentConversationId ? "scale(1.02)" : "scale(1)",
                  boxShadow: "none"
                }}
                actions={[
                  <Popconfirm
                    title="Delete this conversation?"
                    onConfirm={() => handleDeleteConversation(conv.conversationId)}
                    okText="Yes"
                    cancelText="No"
                  >
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                    />
                  </Popconfirm>,
                ]}
                onClick={() => loadConversation(conv.conversationId)}
              >
                <List.Item.Meta
                  avatar={
                    <Button 
                      type="primary" 
                      shape="circle" 
                      size="small" 
                      icon={<MessageOutlined />} 
                      style={{ 
                        background: conv.conversationId === currentConversationId 
                          ? colors.border 
                          : "#e8e8e8",
                        color: conv.conversationId === currentConversationId ? "white" : "#201F1F",
                        border: "none"
                      }} 
                    />
                  }
                  title={
                    <Text 
                      strong 
                      style={{ 
                        fontSize: "13px",
                        color: "#201F1F"
                      }}
                    >
                      {conv.title}
                    </Text>
                  }
                  description={
                    <Text 
                      type="secondary" 
                      style={{ 
                        fontSize: "11px",
                        color: "#666"
                      }}
                    >
                      {new Date(conv.updatedAt).toLocaleDateString()}
                    </Text>
                  }
                />
              </List.Item>
            )}
          />
        )}
        </div>
      </Sider>

      {/* Main Chat Area */}
      <Content style={{ display: "flex", flexDirection: "column", background: "#FFF7F1" }}>
        {/* Chat Messages */}
        <div
          style={{
            flex: 1,
            padding: "20px",
            overflowY: "auto",
            background: "#FFF7F1",
          }}
          className="custom-scrollbar"
        >
          {messages.length === 0 && (
            <div style={{ 
              textAlign: "center", 
              marginTop: "100px",
              color: "#201F1F"
            }}>
              <Title level={4} style={{ color: "#201F1F", marginBottom: "8px" }}>
                {botType === 'GEM' ? 'Welcome to GEM' : 'Welcome to CLEO'}
              </Title>
              <Text style={{ fontSize: "16px", color: "#201F1F" }}>
                {botType === 'GEM' 
                  ? 'Your Goal Execution Manager is ready to optimize performance and drive results.'
                  : 'Your Comprehensive Leadership Execution Orchestrator is ready to help.'
                }
              </Text>
            </div>
          )}

          {messages.map((msg, index) => (
            <div
              key={index}
              style={{
                display: "flex",
                justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  maxWidth: "70%",
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: msg.role === "user" ? "row-reverse" : "row",
                }}
              >
                <Avatar
                  style={{
                    backgroundColor: msg.role === "user" ? colors.primary : colors.border,
                    margin: msg.role === "user" ? "0 0 0 8px" : "0 8px 0 0",
                    flexShrink: 0,
                  }}
                  icon={msg.role === "user" ? <UserOutlined /> : <RobotOutlined />}
                />
                <div
                  style={{
                    background: msg.role === "user" ? colors.primary : "white",
                    color: msg.role === "user" ? "white" : "#333",
                    padding: "12px 16px",
                    borderRadius: "18px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    border: msg.role === "assistant" ? "1px solid #e8e8e8" : "none",
                  }}
                >
                  {msg.role === "assistant" ? (
                    <>
                      {(() => {
                        const parsed = ChartParser.parse(msg.content);
                        const textContent = parsed.text || msg.content;
                        
                        return (
                          <>
                            <SafeMarkdown components={getMarkdownComponents(colors)}>
                              {textContent}
                            </SafeMarkdown>
                            {parsed.hasChart && (
                              <div style={{ marginTop: '16px' }}>
                                <ChartRenderer 
                                  type={parsed.type} 
                                  data={parsed.data} 
                                  config={{ width: 800, height: 600 }} 
                                />
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  ) : (
                    <Text style={{ color: "white" }}>{msg.content}</Text>
                  )}
                  <div style={{ 
                    fontSize: "11px", 
                    opacity: 0.7, 
                    marginTop: "4px",
                    color: msg.role === "user" ? "rgba(255,255,255,0.8)" : "#999"
                  }}>
                    {new Date(msg.timestamp).toLocaleTimeString()}
                    {msg.model && ` • ${msg.model}`}
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {loading && (
            <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: "16px" }}>
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <Avatar
                  style={{
                    backgroundColor: colors.border,
                    marginRight: "8px",
                  }}
                  icon={<RobotOutlined />}
                />
                <div
                  style={{
                    background: "white",
                    padding: "12px 16px",
                    borderRadius: "18px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    border: "1px solid #e8e8e8",
                  }}
                >
                  <Spin size="small" />
                  <Text style={{ marginLeft: "8px", color: "#666" }}>Thinking...</Text>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div style={{ 
          padding: "0px 50px 0px 10px", 
          borderTop: "2px solid rgba(241, 90, 36, 0.38)",
          background: "white",
          maxWidth: "1350px",
          margin: "0 auto",
          width: "100%"
        }}>
          <EnhancedChatInput
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onSend={handleSendMessage}
            loading={loading}
            placeholder="Ask me anything about your performance..."
            colors={colors}
          />
        </div>
      </Content>




    </Layout>
  );
}

export default ChatInterfaceWithHistory;